/**
 * Providers Component
 * Configures all global providers: QueryClient, Theme, Cart, and I18n.
 *
 * Note: IntlProvider is wired in LocaleLayout (App.tsx) because it
 * depends on the locale extracted from the URL via React Router params.
 * I18n initializes eagerly at module level (via client.ts); Providers
 * waits for translations to be loaded before rendering the tree.
 */

import { ReactNode, useState, useEffect } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ThemeProvider } from '@template-project/afrodita-components';
import '@template-project/afrodita-components/theme/theme-variables.css';
import { DeviceProvider } from '@template-project/storefront-shared';
import { CartProvider } from '@/lib/context/cart-context';
import { CampaignProvider } from '@template-project/storefront-campaign';
import { CheckoutReturnListener } from '@/components/CheckoutReturnListener/CheckoutReturnListener';
import { CheckoutAbandonedModal } from '@/components/CheckoutAbandonedModal/CheckoutAbandonedModal';
import { FunnelProvider } from '@template-project/storefront-funnel';
import { layoutFunnelAnalytics } from '@/lib/funnel-analytics-bridge';
import { env } from '@/lib/utils/env';
import { productService } from '@/infrastructure/config/services.config';
import { defaultTheme } from '@/styles/theme/default-theme';
import { i18nReady } from '@/lib/i18n';

export function Providers({ children }: { children: ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: {
            staleTime: 60 * 1000,
            refetchOnWindowFocus: true,
            retry: 1,
          },
        },
      })
  );

  const [translationsReady, setTranslationsReady] = useState(false);

  useEffect(() => {
    document.documentElement.classList.remove('dark');
    localStorage.removeItem('tf-theme');
  }, []);

  useEffect(() => {
    i18nReady.then(() => setTranslationsReady(true));
  }, []);

  if (!translationsReady) return null;

  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider theme={defaultTheme}>
        <DeviceProvider>
          <CartProvider>
            <CheckoutReturnListener />
            <CheckoutAbandonedModal />
            <CampaignProvider
              config={{
                apiBaseUrl: env.CAMPAIGNS_API_URL,
                tenantId: env.CAMPAIGNS_TENANT_ID,
                templatesApiBaseUrl: env.FUNNELS_API_URL,
              }}
            >
              <FunnelProvider
                config={{
                  apiBaseUrl: env.FUNNELS_API_URL,
                  tenantId: env.FUNNELS_TENANT_ID,
                  checkoutMiddlewareUrl: env.CHECKOUT_MIDDLEWARE_URL,
                }}
                runtimeVersion={env.RUNTIME_VERSION}
                analytics={layoutFunnelAnalytics}
                resolveProductImage={async (productId) => {
                  const p = await productService.getProductById(productId).catch(() => null);
                  return p?.images?.[0]?.src;
                }}
              >
                {children}
              </FunnelProvider>
            </CampaignProvider>
          </CartProvider>
        </DeviceProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
}
